import api from "../axios";
import { updatePatientNote } from "../patient/details";

const getAccountId = () => sessionStorage.getItem("acc_id");
const getLocationId = () =>
  JSON.parse(sessionStorage.getItem("current_loc") || "{}")?.id;

const buildReportParams = ({ startDate, endDate, provider, page, pageSize } = {}) => {
  const params = { location: getLocationId() };

  
  if (startDate && endDate) {
    params.start_date = startDate;
    params.end_date = endDate;
  }
  if (provider != null) params.provider = provider;
  if (page != null) params.page = page;
  if (pageSize != null) params.page_size = pageSize;

  return params;
};

const reportsBase = () => `/rpm/${getAccountId()}/out-of-range/reports`;
const extractData = (response) => response?.data;
const postJson = async (url, body = {}) => extractData(await api.post(url, body));

export const getCategoryReport = async (options = {}) => {
  const accountId = getAccountId();
  const response = await api.get(
    `/rpm/${accountId}/out-of-range/category-report/`,
    { params: buildReportParams(options) }
  );
  return extractData(response);
};

export const getListReport = async (options = {}) => {
  const accountId = getAccountId();
  const response = await api.get(
    `/rpm/${accountId}/out-of-range/list-report/`,
    { params: buildReportParams(options) }
  );
  return extractData(response);
};

// SCRUM-790: read-only preview of the location's current Extended Threshold
// cycle (same object as a Draft's snapshot; nothing is saved). The report is
// per location, so only `location` is sent.
export const getExtendedThresholdReport = async () => {
  const accountId = getAccountId();
  const response = await api.get(
    `/rpm/${accountId}/out-of-range/extended-threshold-report/`,
    { params: { location: getLocationId() } }
  );
  return extractData(response);
};

export const saveReviewNote = async ({ noteId, text, patientId } = {}) => {
  if (!noteId) return null;
  return updatePatientNote(noteId, text, patientId, getLocationId());
};

const makeGenerateReportBody = ({
  location,
  provider,
  startDate,
  endDate,
  reportView,
  exportType,
} = {}) => {
  const body = { location, report_view: reportView };

  if (startDate && endDate) {
    body.start_date = startDate;
    body.end_date = endDate;
  }
  if (provider != null) body.provider = provider;
  if (exportType != null) body.export_type = exportType;

  return body;
};

export const generateReport = async (options = {}) =>
  postJson(`${reportsBase()}/generate/`, makeGenerateReportBody(options));

export const getGeneratedReport = async (reportId) =>
  extractData(await api.get(`${reportsBase()}/${reportId}/`));

export const removeReportPatient = async (reportId, { patientId, note } = {}) => {
  const body = { patient_id: patientId };
  if (note) body.note = note;
  return postJson(`${reportsBase()}/${reportId}/remove-patient/`, body);
};

export const postReportAuditEvent = async (
  reportId,
  { eventType, note, metadata } = {}
) => {
  const body = { event_type: eventType };
  if (note) body.note = note;
  if (metadata) body.metadata = metadata;
  return postJson(`${reportsBase()}/${reportId}/audit-event/`, body);
};

const postReportStatus = async (reportId, path, { note } = {}) =>
  postJson(`${reportsBase()}/${reportId}/${path}`, note ? { note } : {});

export const markReportSubmitted = async (reportId, options = {}) =>
  postReportStatus(reportId, "mark-submitted/", options);

export const closeReport = async (reportId, options = {}) =>
  postReportStatus(reportId, "close/", options);

export const closeAndSubmitReport = async (reportId, options = {}) =>
  postReportStatus(reportId, "close-and-submit/", options);

export const shareGeneratedReport = async (
  reportId,
  { blob, filename, format, recipientType, recipientId, recipientEmail, note } = {}
) => {
  const mime =
    format === "pdf"
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const file = new File([blob], filename, { type: blob.type || mime });

  const formData = new FormData();
  formData.append("file", file, filename);
  formData.append("format", format);
  formData.append("recipient_type", recipientType);
  formData.append("recipient_id", recipientId);
  if (recipientEmail) formData.append("recipient_email", recipientEmail);
  if (note) formData.append("note", note);

  const response = await api.post(`${reportsBase()}/${reportId}/share/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return extractData(response);
};
